import {
  isCapitalizedMidSentence,
  letterTokens,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * The Vocabulary Profiler: how much of a text is covered by each frequency band of a
 * word list, and which words fall outside every list.
 *
 * Two lists, both by Browne, Culligan and Phillips (CC BY-SA 4.0):
 *
 * - the **New General Service List** (NGSL 1.2): 2,809 lemmas of general English in
 *   frequency order (`public/data/ngsl-1.2.txt`, adapted from the authors' files; its
 *   header carries the licence and the changes). Frequency order is the list's own
 *   Standardized Frequency Index rank. It is cut here into the first 1,000, the second
 *   1,000 and the remaining 809 lemmas — a conventional reading of a frequency-ranked
 *   list, not a division the authors publish. The NGSL also has 52 supplementary words
 *   (days, months, numbers) that belong to it but carry no ranking; they form a band of
 *   their own;
 * - the **New Academic Word List** (NAWL 1.2), already in the repository for the
 *   Academic Vocabulary Checker (`writing-analysis/nawl.ts`): the academic words that
 *   are not among the NGSL's.
 *
 * A word is counted against the first list that holds it, NGSL then NAWL, and what
 * neither holds is "off-list". Matching is by the lists' own word forms (a lemma with
 * its inflections, British and American spellings included), the way the lists were
 * built; a text is split into running words at spaces, punctuation and hyphens, and a
 * contraction is read by its stem ("don't" as "don", which the NGSL files under "do").
 * Capitalized words in mid-sentence that no list holds are treated as names or
 * acronyms and, by default, left out of the count.
 */

export const NGSL_URL = "/data/ngsl-1.2.txt";

export type Band =
  | "first"
  | "second"
  | "third"
  | "supplementary"
  | "academic"
  | "off";

export const BANDS: readonly Band[] = [
  "first",
  "second",
  "third",
  "supplementary",
  "academic",
  "off",
];

/** The last rank of the first and second bands. */
export const BAND_EDGES = { first: 1000, second: 2000 } as const;

const RE_LINE_BREAK = /\r?\n/;
const RE_COMBINING = /\p{M}/gu;

/** Accents folded the way the lists are: "café" → "cafe". */
function fold(word: string): string {
  return word.normalize("NFD").replace(RE_COMBINING, "");
}

function bandOfRank(rank: number): Band {
  if (rank <= BAND_EDGES.first) {
    return "first";
  }
  return rank <= BAND_EDGES.second ? "second" : "third";
}

export interface Lexicon {
  /** Word form → the NGSL lemma it belongs to and that lemma's band. */
  ngsl: ReadonlyMap<string, { lemma: string; band: Band }>;
  /** NGSL lemmas in the file. */
  ngslLemmas: number;
  /** Supplementary words (days, months, numbers) in the file. */
  supplementary: number;
  /** The NAWL entry for a word, if any. */
  academicLemma: (word: string) => string | undefined;
}

/** The NGSL file's text → word forms with their lemma and band. First claim wins. */
export function parseNgsl(text: string): {
  forms: Map<string, { lemma: string; band: Band }>;
  lemmas: number;
  supplementary: number;
} {
  const forms = new Map<string, { lemma: string; band: Band }>();
  let rank = 0;
  let supplementary = 0;
  for (const line of text.split(RE_LINE_BREAK)) {
    if (!line || line.startsWith("#")) {
      continue;
    }
    const extra = line.startsWith("~");
    const [lemma, ...rest] = (extra ? line.slice(1) : line).split(",");
    if (extra) {
      supplementary += 1;
    } else {
      rank += 1;
    }
    const band = extra ? "supplementary" : bandOfRank(rank);
    for (const form of [lemma, ...rest]) {
      if (form && !forms.has(form)) {
        forms.set(form, { lemma, band });
      }
    }
  }
  return { forms, lemmas: rank, supplementary };
}

export function buildLexicon(
  ngslText: string,
  academicLemma: (word: string) => string | undefined
): Lexicon {
  const { forms, lemmas, supplementary } = parseNgsl(ngslText);
  return { ngsl: forms, ngslLemmas: lemmas, supplementary, academicLemma };
}

let loading: Promise<Lexicon> | null = null;

/**
 * Both lists, fetched once per page load: the NGSL file from `public/data`, and the NAWL
 * as a lazily loaded module so a visitor who only reads the page never downloads either.
 * A failed load is forgotten, so a retry asks again.
 */
export function loadLexicon(): Promise<Lexicon> {
  if (!loading) {
    loading = Promise.all([
      fetch(NGSL_URL).then((response) => {
        if (!response.ok) {
          throw new Error(`NGSL returned ${response.status}`);
        }
        return response.text();
      }),
      import("@/lib/tool-kit/writing-analysis/academic-vocabulary"),
    ])
      .then(([text, academic]) => buildLexicon(text, academic.lemmaOf))
      .catch((error: unknown) => {
        loading = null;
        throw error;
      });
  }
  return loading;
}

/* ------------------------------------------------------------------ profile */

export interface Classified {
  band: Band;
  /** The list entry the word counts under, or the word itself when off-list. */
  lemma: string;
}

const RE_NOT_TAIL = /n't$/;
/** Contractions whose stem is not the word: can't → can, won't → will. */
const CONTRACTION_STEMS: Readonly<Record<string, string>> = {
  "can't": "can",
  "won't": "will",
  "shan't": "shall",
};

/** The forms to try for a word: itself, then what a contraction or possessive stands on. */
function candidates(form: string): string[] {
  if (!form.includes("'")) {
    return [form];
  }
  return [
    form,
    CONTRACTION_STEMS[form] ?? form.replace(RE_NOT_TAIL, ""),
    form.split("'")[0],
  ];
}

/** Which list holds a word form (lower case, straight apostrophes). */
export function classify(lower: string, lexicon: Lexicon): Classified {
  const form = fold(lower);
  for (const candidate of candidates(form)) {
    const hit = lexicon.ngsl.get(candidate);
    if (hit) {
      return { band: hit.band, lemma: hit.lemma };
    }
  }
  const academic = lexicon.academicLemma(form);
  return academic
    ? { band: "academic", lemma: academic }
    : { band: "off", lemma: form };
}

export interface BandRow {
  band: Band;
  /** Running words in this band. */
  tokens: number;
  /** Different lemmas (list entries), or different words for the off-list band. */
  distinct: number;
  /** Share of the counted running words, 0–1. */
  coverage: number;
  /** Share covered by this band and every band above it, 0–1. */
  cumulative: number;
}

export interface WordCount {
  word: string;
  count: number;
}

export interface BandMatch {
  start: number;
  end: number;
  band: Band;
}

export interface Profile {
  /** Running words counted (names and acronyms left out when asked). */
  tokens: number;
  /** Capitalized mid-sentence words that no list holds, left out of the count. */
  names: number;
  rows: BandRow[];
  /** Words on no list, most frequent first. */
  offList: WordCount[];
  /** Academic list entries used, most frequent first. */
  academic: WordCount[];
  /** Spans of academic and off-list words, for highlighting. */
  matches: BandMatch[];
}

export interface ProfileOptions {
  /** Leave capitalized mid-sentence words that no list holds out of the count. */
  skipNames: boolean;
}

function ranked(counts: ReadonlyMap<string, number>): WordCount[] {
  return [...counts]
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count || a.word.localeCompare(b.word));
}

export function profileText(
  text: string,
  lexicon: Lexicon,
  options: ProfileOptions
): Profile {
  const tokens = Object.fromEntries(BANDS.map((band) => [band, 0])) as Record<
    Band,
    number
  >;
  const lemmas = new Map<Band, Set<string>>(
    BANDS.map((band) => [band, new Set<string>()])
  );
  const offList = new Map<string, number>();
  const academic = new Map<string, number>();
  const matches: BandMatch[] = [];
  let names = 0;
  let counted = 0;
  for (const token of letterTokens(text)) {
    const { band, lemma } = classify(token.lower, lexicon);
    if (
      band === "off" &&
      options.skipNames &&
      isCapitalizedMidSentence(text, token.start)
    ) {
      names += 1;
      continue;
    }
    counted += 1;
    tokens[band] += 1;
    lemmas.get(band)?.add(lemma);
    if (band === "off") {
      offList.set(lemma, (offList.get(lemma) ?? 0) + 1);
    } else if (band === "academic") {
      academic.set(lemma, (academic.get(lemma) ?? 0) + 1);
    }
    if (band === "off" || band === "academic") {
      matches.push({ start: token.start, end: token.end, band });
    }
  }
  let running = 0;
  const rows = BANDS.map((band) => {
    running += tokens[band];
    return {
      band,
      tokens: tokens[band],
      distinct: lemmas.get(band)?.size ?? 0,
      coverage: counted > 0 ? tokens[band] / counted : 0,
      cumulative: counted > 0 ? running / counted : 0,
    };
  });
  return {
    tokens: counted,
    names,
    rows,
    offList: ranked(offList),
    academic: ranked(academic),
    matches,
  };
}
